import React from "react";
import { useLocalization } from "@fluent/react";
import { Button, ResponsiveFlex, Row } from "@narmi/design_system";
import { FormHeaderWithIcon } from "cerulean";
import styles from "./LoanPaymentReceipt.module.scss";

export type LoanReceiptData = {
  loanDescription: string;
  fromDescription: string | undefined;
  amount: string;
  date: string;
  frequency?: string;
  memo?: string;
  isScheduled: boolean;
};

interface LoanPaymentReceiptProps {
  data: LoanReceiptData;
}

const ReceiptRow = ({
  name,
  value,
}: {
  name: string;
  value: string | undefined;
}) => (
    <Row>
      <Row.Item shrink>
        <p className={styles.rowLabel}>{name}</p>
      </Row.Item>
      <Row.Item>
        <p className={styles.rowValue}>
          {value || "-"}
        </p>
      </Row.Item>
  </Row>
);

const LoanPaymentReceipt = ({ data }: LoanPaymentReceiptProps) => {
  const { l10n } = useLocalization();

  const heading = data.isScheduled
    ? l10n.getString("loan-payment-receipt-scheduled-heading")
    : l10n.getString("loan-payment-receipt-sent-heading");

  const dateLabel = data.frequency
    ? l10n.getString("label-review-date-recurring")
    : l10n.getString("label-review-date-once");

  return (
    <div className="loan-payment-page">
      <FormHeaderWithIcon
        headerText={heading}
        iconComponent={
          <span className={`narmi-icon-check margin--left--xs ${styles.checkIcon}`} />
        }
      />
      <ResponsiveFlex gapSize="m">
        <div className={`bgColor--white rounded--all ${styles.card}`}>
          <div className={styles.receiptBody}>
            <h4 className={`nds-sans ${styles.title}`}>
              {l10n.getString("loan-paydown-payment-title", {
                loan: data.loanDescription,
              })}
            </h4>
            <div className="padding--all bgColor--success rounded--all">
              <ReceiptRow
                name={l10n.getString("label-from")}
                value={data.fromDescription}
              />
              <ReceiptRow
                name={l10n.getString("label-amount")}
                value={data.amount}
              />
              {data.frequency && (
                <ReceiptRow
                  name={l10n.getString("label-frequency")}
                  value={data.frequency}
                />
              )}
              <ReceiptRow name={dateLabel} value={data.date} />
              {data.memo && (
                <ReceiptRow
                  name={l10n.getString("label-memo")}
                  value={data.memo}
                />
              )}
            </div>
            <div className={styles.primaryButton}>
              <div className={styles.button}>
                <Button
                  label={l10n.getString("label-done")}
                  testId="loan-receipt-done"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                />
              </div>
            </div>
            <div className={styles.secondaryButton}>
              <div className={styles.button}>
                <Button
                  kind="plain"
                  label={l10n.getString("ach-payment-receipt-cta")}
                  testId="loan-receipt-make-another"
                  onClick={() => {
                    window.location.href = "/transfer/loans";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </ResponsiveFlex>
    </div>
  );
};

export default LoanPaymentReceipt;
